<template>
  <PageMeta :meta="pageMeta" />
  <MainContent>
    <div class="one-day-vacancy-detail-page detail-page">
      <div class="banner-container">
        <ClientOnly>
          <AppImage
            v-if="fullOneDayVacancyInfo.id"
            ref="bannerImage"
            :path="returnOneDayVacancyBanner"
            format="banner"
            :alt="$gadTranslate('general_alt_banner_image')"
            :background-image="true"
            class="banner"
            :image-exists="returnImageExists"
            :updated="fullOneDayVacancyInfo.updated"
            @image-uploaded="bannerUploaded()"
          >
            <template #actions>
              <div
                v-if="!statusEquals(fullOneDayVacancyInfo.status, STATUSES.DRAFT)"
                class="actions"
              >
                <ActionBar
                  :key="likeKey"
                  :image-path="returnOneDayVacancyBanner"
                  :image-updated="fullOneDayVacancyInfo.updated"
                  :title-to-share="fullOneDayVacancyInfo.name"
                  :subtitle-one="organisationInfo?.name"
                  :subtitle-two="formatAddressStreet(fullOneDayVacancyInfo.address) + ' ' + formatAddressMunicipality(fullOneDayVacancyInfo.address)"
                  liked-type="one_day_vacancy"
                  :liked-id="fullOneDayVacancyInfo.id"
                  :initial-liked="fullOneDayVacancyInfo.isLikedByCurrentUser"
                  :like-count="fullOneDayVacancyInfo.likeCount"
                  :on-like-anon-click="openLikeModal"
                />
              </div>
            </template>
            <template #fallback-image>
              <div class="banner-fallback">
                <img :src="fallbackImageSrc">
              </div>
            </template>
          </AppImage>
        </ClientOnly>
      </div>
      <div class="contents max-width-container">
        <StickyEditButton
          v-if="editable && showEditButton"
          @click="openOneDayVacancyOverlay()"
          @blur-changed="blur=$event"
        >
          {{ $gadTranslateUpper('general_modify') }}
        </StickyEditButton>
        <div class="cols-container">
          <div
            class="left-col"
            :class="blur ? 'blur-content' : ''"
          >
            <BCard>
              <div class="mobile-interest mobile-only">
                <div class="interest-wrapper">
                  <InterestSelection
                    ref="interestSelection"
                    v-model="fullOneDayVacancyInfo.interestIds"
                    :editable="false"
                    :show-num-selected-text="false"
                    :use-ids="true"
                    optional-icons-key="select_interest_component_optional_max_three"
                  />
                </div>
              </div>
              <div class="breadcrumb small">
                <GadBreadcrumb
                  v-if="getBreadcrumbItems"
                  :items="getBreadcrumbItems"
                />
              </div>
              <div class="detail-header">
                <OrganisationAvatar
                  v-if="organisationInfo"
                  :organisation="organisationInfo"
                  class="mr-4"
                />

                <div class="interest-wrapper desktop-only">
                  <InterestSelection
                    ref="interestSelection"
                    v-model="fullOneDayVacancyInfo.interestIds"
                    :editable="false"
                    :show-num-selected-text="false"
                    :use-ids="true"
                    optional-icons-key="select_interest_component_optional_max_three"
                  />
                </div>
              </div>
              <h1>{{ fullOneDayVacancyInfo.name }}</h1>
              <h3
                v-if="fullOneDayVacancyInfo.localPartnerOrganisationName"
                class="no-break"
              >
                {{ $gadTranslate('page_one_day_vacancy_detail_local_partner_organisation_name_title') }}
              </h3>
              <p v-if="fullOneDayVacancyInfo.localPartnerOrganisationName">
                {{ fullOneDayVacancyInfo.localPartnerOrganisationName }}
              </p>
              <h2 class="no-break">{{ $gadTranslate('page_one_day_vacancy_detail_description_title') }}</h2>
              <div
                v-dompurify-html="fullOneDayVacancyInfo.description"
                class="rich-text"
              />
              <div class="one-day-vacancy-grid-wrapper mt-5 mb-5">
                <div
                  v-if="fullOneDayVacancyInfo.activityDate"
                  class="item-holder-wrapper"
                >
                  <i class="fa fa-calendar-alt" />
                  <h3>{{ $gadDate(fullOneDayVacancyInfo.activityDate, 'longDate') }}</h3>
                  <span>{{ $gadTranslateUpper('general_date') }}</span>
                </div>
                <div class="item-holder-wrapper">
                  <i class="fa fa-hands-helping" />
                  <h3>{{ `${fullOneDayVacancyInfo.amountSubscriptions}/${fullOneDayVacancyInfo.maxAmountSubscriptions} ${areSubscriptionsFull ? '(' + $gadTranslateUpper('general_full') + ')' : ''}` }}</h3>
                  <span>{{ $gadTranslateUpper('general_number_of_subscriptions') }}</span>
                </div>
                <div
                  v-if="fullOneDayVacancyInfo.startTime && fullOneDayVacancyInfo.endTime"
                  class="item-holder-wrapper"
                >
                  <i class="fa fa-clock" />
                  <h3>{{ `${$gadTime(fullOneDayVacancyInfo.startTime, 'shortTime')} - ${$gadTime(fullOneDayVacancyInfo.endTime, 'shortTime')} (${timingText})` }}</h3>
                  <span>{{ $gadTranslateUpper('general_timeslot_title') }}</span>
                </div>
              </div>
              <div
                v-if="fullOneDayVacancyInfo.spokenLanguages?.length > 0"
                class="mb-4"
              >
                <h3>
                  {{ $gadTranslate('page_one_day_vacancy_detail_spoken_languages_title') }}
                </h3>

                <Chip
                  v-for="(language, index) in fullOneDayVacancyInfo.spokenLanguages"
                  :key="index"
                  :label="language.name"
                  class="mr-2 mb-2"
                />
              </div>
              <h3
                v-if="fullOneDayVacancyInfo.additionalInfo"
                class="no-break"
              >
                {{ $gadTranslate('page_one_day_vacancy_detail_additional_info_title') }}
              </h3>
              <div
                v-dompurify-html="fullOneDayVacancyInfo.additionalInfo"
                class="rich-text"
              />

              <OrganisationBar
                v-if="organisationInfo"
                :organisation="organisationInfo"
                class="no-break"
              />
            </BCard>

            <div class="mt-4 text-center">
              <ButtonWithIcon
                v-if="canSubscribe"
                icon="fa-hands-helping"
                variant="primary"
                class="mb-3 desktop-only"
                @click="openSubscriptionModal"
              >
                {{ $gadTranslate('page_one_day_vacancy_detail_im_interested') }}
              </ButtonWithIcon>
            </div>
          </div>

          <div
            class="right-col no-break"
            :class="blur ? 'blur-content' : ''"
          >
            <BCard>
              <template v-if="fullOneDayVacancyInfo.activityDate">
                <IconWithText
                  icon="fa-calendar-alt"
                  class="mt-2 mb-2 ml-2 dates tint"
                >
                  <span class="date">{{ $gadDate(fullOneDayVacancyInfo.activityDate, 'longDate') }}</span>
                </IconWithText>
              </template>

              <template v-if="fullOneDayVacancyInfo.startTime && fullOneDayVacancyInfo.endTime">
                <IconWithText
                  icon="fa-clock"
                  class="mt-2 mb-2 ml-2 dates tint"
                >
                  <span class="date">{{ `${$gadTime(fullOneDayVacancyInfo.startTime, 'shortTime')} - ${$gadTime(fullOneDayVacancyInfo.endTime, 'shortTime')}` }}</span>
                </IconWithText>
              </template>

              <template v-if="fullOneDayVacancyInfo.isDigital">
                <IconWithText
                  icon="fa-laptop"
                  class="mb-4 ml-2 dates tint"
                >
                  {{ $gadTranslateUpper('page_one_day_vacancy_detail_label_digital') }}
                </IconWithText>
              </template>

              <BAlert
                v-if="isUserSubscribedAlready"
                variant="info"
                :show="true"
              >
                <i class="fa fa-check mr-2" />{{ $gadTranslateUpper('general_already_subscribed') }}
              </BAlert>
              <BAlert
                v-else-if="areSubscriptionsFull"
                variant="info"
                :show="true"
              >
                <i class="fa fa-lock mr-2" />{{ $gadTranslateUpper('general_subscriptions_full') }}
              </BAlert>

              <ButtonWithIcon
                v-if="canSubscribe"
                block
                icon="fa-hands-helping"
                variant="primary"
                class="mb-3 desktop-only"
                @click="openSubscriptionModal"
              >
                {{ $gadTranslate('page_one_day_vacancy_detail_im_interested') }}
              </ButtonWithIcon>

              <template v-if="fullOneDayVacancyInfo.contactPersonUserId">
                <IconWithText
                  icon="fa-user"
                  class="mb-4 ml-2 dates tint"
                >
                  <span>
                    {{ `${fullOneDayVacancyInfo.contactPersonFirstName} ${fullOneDayVacancyInfo.contactPersonFamilyName} ` }}<a
                      class="small clickable-phone"
                      href="#"
                      @click.prevent="showContactPersonPhone"
                    >({{ $gadTranslateUpper('general_phone_short') }}:<i class="fa fa-phone" />)</a>
                  </span>
                </IconWithText>
              </template>

              <ClientOnly>
                <LeafletMap
                  :markers="mapMarkers"
                  class="mb-2"
                />
                <template #fallback>
                  <div
                    class="leaflet-map-wrapper mb-2"
                    style="height: 300px"
                  />
                </template>
              </ClientOnly>

              <InlineEditableAddress
                v-model="fullOneDayVacancyInfo.address"
                :editable="false"
                :with-icons="false"
                :placeholder="$gadTranslate('general_enter_address')"
                not-editable-placeholder=""
                :detail-page="true"
              />
            </BCard>

            <div class="print-button-container">
              <ButtonWithIcon
                block
                class="btn-invert mt-4"
                icon="fa-print"
                @click="print"
              >
                {{ $gadTranslateUpper('general_print_page') }}
              </ButtonWithIcon>
            </div>

            <ReportLink
              target-type="one_day_vacancy"
              :target-id="oneDayVacancyId"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="canSubscribe"
      class="mobile-wrapper shadow mobile-only no-print"
    >
      <ButtonWithIcon
        icon="fa-hands-helping"
        variant="primary"
        @click="openSubscriptionModal"
      >
        {{ $gadTranslate('page_one_day_vacancy_detail_im_interested') }}
      </ButtonWithIcon>
    </div>

    <OneDayVacancySuggestionCarousel
      v-if="fullOneDayVacancyInfo.id"
      :one-day-vacancies="suggestedOneDayVacancies"
      :title="$gadTranslateUpper('general_similar_one_day_vacancies')"
      :show-button="true"
    />

    <LazyOneDayVacancyPublishedModal
      v-if="editable"
      ref="oneDayVacancyPublishedModal"
      :one-day-vacancy="fullOneDayVacancyInfo"
      :organisation-info="organisationInfo"
      @print-one-day-vacancy="print()"
      @copy-one-day-vacancy-link="copyOneDayVacancyLink()"
    />

    <LazyOneDayVacancyFormOverlay
      v-if="editable"
      ref="oneDayVacancyFormOverlay"
      :one-day-vacancy-id="oneDayVacancyId"
      @archive-one-day-vacancy="archiveOneDayVacancy"
      @duplicate-one-day-vacancy="duplicateOneDayVacancy"
      @delete-one-day-vacancy="deleteOneDayVacancy"
      @open-overlay-right="openOverlayRight"
      @close-overlay-right="closeOverlayRight"
      @one-day-vacancy-form-submitted="oneDayVacancyFormSubmitted"
    />

    <LazyOneDayVacancySubscriptionModal
      v-if="fullOneDayVacancyInfo.id"
      ref="oneDayVacancySubscriptionModal"
      :one-day-vacancy="fullOneDayVacancyInfo"
      :like-only="isLikeOnly"
      @form-submitted="subscriptionSuccess"
      @user-did-subscribe="userSubscribed"
      @like-success="handleLikeSuccess"
    />

    <GoogleJobPostingJsonOneDayVacancy
      v-if="!$globalState.platform?.id && fullOneDayVacancyInfo.id"
      :one-day-vacancy="fullOneDayVacancyInfo"
      :organisation="organisationInfo"
    />

    <GoogleBreadcrumbJson
      v-if="fullOneDayVacancyInfo.id"
      :items="getBreadcrumbItems"
    />

    <Recaptcha
      ref="recaptcha"
      :render-on-page-load="false"
    />
  </MainContent>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { STATUSES, statusEquals } from 'give-a-day-shared/data/enums/oneDayVacancyEnums.mjs'
import { oneDayVacancyBannerImage, getFullCloudfrontFallbackImage } from 'give-a-day-shared/utils/s3Path.mjs'
import { isSet } from 'give-a-day-shared/utils/isSet.mjs'
import { IMAGE_FORMATS } from 'give-a-day-shared/utils/imageFormats.mjs'
import { getAddressMunicipalityName, formatAddressStreet, formatAddressMunicipality } from 'give-a-day-shared/utils/addressUtils.mjs'
import { PAGE_NOT_FOUND_STATUS_CODE } from 'give-a-day-shared/routes/statusCodes.mjs'
import { LIKED_TYPES } from 'give-a-day-shared/data/enums/likeEnums.mjs'
import Chip from 'primevue/chip'
import { isOrganisationAuthor, isSuperAuthor } from '../../functions/frontendAuthUtils.js'
import { getFullCloudfrontImageUrlForFormat } from '../../functions/imageUtils.js'
import { printOneDayVacancyImpl } from '../../functions/oneDayVacancy.js'
import { waitForRef } from '@/functions/waitUntilRefReady.js'
import { useTimings } from '@/composables/useTimings.js'
import { dropQueryParametersAndRedirect } from '@/functions/urlUtils.js'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import { useCustomPageMetaUrl } from '@/composables/useCustomPageMetaUrl.js'
import { useLike } from '@/composables/useLike.js'
const nuxtApp = useNuxtApp()
const router = useRouter()
const route = useRoute()

const recaptcha = ref()
const showEditButton = ref(true)
const oneDayVacancyFormOverlay = ref(null)
const oneDayVacancyPublishedModal = ref(null)
const oneDayVacancySubscriptionModal = ref()
const isLikeOnly = ref(false)
const likeKey = ref(0)
const oneDayVacancyId = computed(() => Number(route.params.id))
const fullOneDayVacancyInfo = ref({})
const organisationInfo = ref({})
const allOneDayVacIds = ref([])
const suggestedOneDayVacancies = ref([])
const blur = ref(false)
const isUserSubscribedAlready = ref(false)

const { getCustomCanonicalUrl, getCustomAlternateUrls, getCustomUrlParamsWithLocale } = useCustomPageMetaUrl()

const loadData = async () => {
  try {
    fullOneDayVacancyInfo.value = await nuxtApp.$getFromBackend('/fetchOneDayVacancy', { oneDayVacancyId: oneDayVacancyId.value })

    if (nuxtApp.$globalState.toggledOrganisationInfo && nuxtApp.$globalState.toggledOrganisationId) {
      const orgOneDayVacIds = await nuxtApp.$getFromBackend('/fetchAllOrganisationOneDayVacancyIds')
      if (orgOneDayVacIds && orgOneDayVacIds.length > 0) {
        allOneDayVacIds.value = orgOneDayVacIds
      }
    }

    if (fullOneDayVacancyInfo.value) {
      organisationInfo.value = await nuxtApp.$getFromBackend('/fetchOrganisationSummary', { organisationId: fullOneDayVacancyInfo.value.organisationId })
      suggestedOneDayVacancies.value = await nuxtApp.$getFromBackend('/fetchAllOrganisationSuggestedOneDayVacancies', { organisationId: fullOneDayVacancyInfo.value.organisationId, oneDayVacancyId: fullOneDayVacancyInfo.value.id })
      if (nuxtApp.$auth.user && !nuxtApp.$globalState.toggledOrganisationId) {
        isUserSubscribedAlready.value = await nuxtApp.$getFromBackend('/checkUserSubscribedToOneDayVacancy', { oneDayVacancyId: fullOneDayVacancyInfo.value.id })
      }
    }
  } catch (err) {
    if (err.statusCode === PAGE_NOT_FOUND_STATUS_CODE) {
      nuxtApp.$showPageNotFoundError()
    } else {
      throw err
    }
  }
}

await loadData()

onMounted(async () => {
  await nextTick()

  const newOneDayVacancy = route.query.new
  if (newOneDayVacancy) {
    await nextTick(() => {
      if (oneDayVacancyFormOverlay.value) {
        oneDayVacancyFormOverlay.value.open(true)
      }
    })
    await dropQueryParametersAndRedirect(nuxtApp, ['new'])
  }

  const openEdit = route.query.openedit
  if (openEdit) {
    await nextTick(() => {
      if (oneDayVacancyFormOverlay.value) {
        oneDayVacancyFormOverlay.value.open(false)
      }
    })
    await dropQueryParametersAndRedirect(nuxtApp, ['openedit'])
  }

  const subscribeToOneDayVacancy = route.query.subscribe
  if (subscribeToOneDayVacancy && !editable.value && statusEquals(fullOneDayVacancyInfo.value.status, STATUSES.ACTIVE) && !nuxtApp.$globalState.toggledOrganisationInfo) {
    await nextTick(() => {
      oneDayVacancySubscriptionModal.value.show()
    })
    await dropQueryParametersAndRedirect(nuxtApp, ['subscribe'])
  }

  if (route.query.like) {
    if (!statusEquals(fullOneDayVacancyInfo.value.status, STATUSES.DRAFT)) {
      try {
        const { addLike } = useLike()
        await addLike({ likedType: LIKED_TYPES.ONE_DAY_VACANCY, likedId: fullOneDayVacancyInfo.value.id })
        handleLikeSuccess()
        nuxtApp.$toast(nuxtApp.$gadTranslate('general_like_button_is_liked_toast'))
      } catch {
        nuxtApp.$toastFailure(nuxtApp.$gadTranslate('general_something_went_wrong'))
      }
      await dropQueryParametersAndRedirect(nuxtApp, ['like'])
    }
  }

  if (route.query.ssoLogin) {
    const { ssoLogin, ...query } = route.query
    await router.replace({ query })
    await oneDayVacancySubscriptionModal.value.handleSSOSubscribe(fullOneDayVacancyInfo.value)
  }
})

const isOrgAuthor = computed(() => {
  return organisationInfo.value.id && isOrganisationAuthor(nuxtApp.$auth.user, organisationInfo.value.id)
})

const editable = computed(() => {
  if (isSuperAuthor(nuxtApp.$auth.user)) {
    return true
  }
  if (fullOneDayVacancyInfo.value) {
    return isOrgAuthor.value || allOneDayVacIds.value.includes(oneDayVacancyId.value)
  }
  return false
})

const pageMeta = computed(() => {
  const organisationName = organisationInfo.value?.name
  let municipalityName = getAddressMunicipalityName(fullOneDayVacancyInfo.value.address)

  return {
    title: nuxtApp.$gadTranslateUpper('meta_title_one_day_vacancy_detail', { organisationName, municipalityName }),
    description: `${nuxtApp.$gadTranslateUpper('meta_desc_one_day_vacancy_detail', { organisationName })} ${nuxtApp.$getCurrentCountryId() === 19 ? nuxtApp.$gadTranslateUpper('meta_desc_general_largest_matching_platform_belgium') : ''}`,
    image: bannerPath,
    ogTitle: fullOneDayVacancyInfo.value.name || null,
    ogDescription: fullOneDayVacancyInfo.value.description || null,
    urlParamsWithLocale: getCustomUrlParamsWithLocale(fullOneDayVacancyInfo.value.id, fullOneDayVacancyInfo.value.nameLocalisations, fullOneDayVacancyInfo.value.address?.countryId, 'oneDayVacancy'),
    customCanonicalUrl: getCustomCanonicalUrl(fullOneDayVacancyInfo.value.id, fullOneDayVacancyInfo.value.nameLocalisations, fullOneDayVacancyInfo.value.address?.countryId, 'oneDayVacancy'),
    customAlternateUrls: getCustomAlternateUrls(fullOneDayVacancyInfo.value.id, fullOneDayVacancyInfo.value.nameLocalisations, fullOneDayVacancyInfo.value.address?.countryId, 'oneDayVacancy'),
  }
})

const bannerPath = computed(() => {
  if (fullOneDayVacancyInfo.value.bannerExistsOnS3) {
    let path = oneDayVacancyBannerImage(fullOneDayVacancyInfo.value.organisationId, fullOneDayVacancyInfo.value.id)

    let format = IMAGE_FORMATS.banner
    return getFullCloudfrontImageUrlForFormat(format, path, format.widths[1], nuxtApp, fullOneDayVacancyInfo.value.updated)
  } else {
    return ''
  }
})

const getBreadcrumbItems = computed(() => {
  const fallback = [
    { label: nuxtApp.$gadTranslateUpper('nav_general_home'), route: '/' },
    { label: nuxtApp.$gadTranslateUpper('general_search_one_day_voluntary_work'), route: '/eendags-vrijwilligerswerk-zoeken' },
    { label: nuxtApp.$gadTranslateUpper('nav_general_vacancy'), seoLabel: fullOneDayVacancyInfo.value?.name || '', seoRoute: '/eendags-vrijwilligerswerk/:oneDayVacancyName/:id', params: { id: fullOneDayVacancyInfo.value?.id, oneDayVacancyName: fullOneDayVacancyInfo.value?.name || '' } },
  ]
  return useBreadcrumb(fallback).getBreadcrumbItems()
})

const areSubscriptionsFull = computed(() => {
  return fullOneDayVacancyInfo.value.amountSubscriptions >= fullOneDayVacancyInfo.value.maxAmountSubscriptions
})

// A local company item is internal to its company: a logged-in visitor who does not belong to that platform may not
// subscribe. An anonymous visitor does keep the button, because opening the modal is the only way to start the SSO
// flow that makes them a member in the first place. The subscribe routes enforce all of this server-side too.
const canSubscribe = computed(() => {
  if (editable.value || !statusEquals(fullOneDayVacancyInfo.value.status, STATUSES.ACTIVE) || nuxtApp.$globalState.toggledOrganisationInfo || isUserSubscribedAlready.value || areSubscriptionsFull.value) {
    return false
  }
  if (!fullOneDayVacancyInfo.value.isLocalCompanyItem) {
    return true
  }
  if (!nuxtApp.$auth.user) {
    return true
  }
  return fullOneDayVacancyInfo.value.viewerIsPlatformMember
})

async function openOneDayVacancyOverlay () {
  const formOverlay = await waitForRef(oneDayVacancyFormOverlay)
  formOverlay.open(false)
  showEditButton.value = false
  blur.value = true
}

const returnOneDayVacancyBanner = computed(() => {
  let path = oneDayVacancyBannerImage(fullOneDayVacancyInfo.value.organisationId, fullOneDayVacancyInfo.value.id)

  return path
})
const returnImageExists = computed(() => {
  return fullOneDayVacancyInfo.value.bannerExistsOnS3
})
const fallbackImageSrc = computed(() => {
  return getFullCloudfrontFallbackImage('volunteers_wide.png')
})
const bannerUploaded = () => {
  fullOneDayVacancyInfo.value.bannerExistsOnS3 = true
}

const startTime = computed(() => fullOneDayVacancyInfo.value.startTime)
const endTime = computed(() => fullOneDayVacancyInfo.value.endTime)
const { timingText } = useTimings(startTime, endTime)

const showContactPersonPhone = async () => {
  const recaptchaToken = await recaptcha.value.getToken()
  const contactPersonPhone = await nuxtApp.$postToBackend('/fetchOneDayVacancyContactPersonPhone', { oneDayVacancyId: fullOneDayVacancyInfo.value.id, recaptchaToken })
  nuxtApp.$swal({
    text: contactPersonPhone,
    confirmButtonText: nuxtApp.$gadTranslateUpper('general_ok'),
  })

  recaptcha.value.reset()
}

const mapMarkers = computed(() => {
  if (fullOneDayVacancyInfo.value && fullOneDayVacancyInfo.value.address) {
    const address = fullOneDayVacancyInfo.value.address
    if (isSet(address.lat) && isSet(address.lng)) {
      return [{ lat: address.lat, lng: address.lng }]
    }
  }
  return []
})

const archiveOneDayVacancy = async () => {
  let title = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_archive_title')
  let text = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_archive_text')
  let successMessage = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_archive_success')
  let failureMessage = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_archive_fail')

  await nuxtApp.$confirm(title, text, async () => {
    let result = await nuxtApp.$postToBackend('/archiveOneDayVacancies', { oneDayVacancyIds: [fullOneDayVacancyInfo.value.id], organisationId: nuxtApp.$globalState.toggledOrganisationId, dataToShow: 'show_all' })
    await nuxtApp.$router.push(nuxtApp.$findPath('/organisatie/eendags-vacatures-en-matches'))
    if (result.successCounter > 0) {
      nuxtApp.$toast(successMessage)
    } else {
      nuxtApp.$toastFailure(failureMessage)
    }
  })
}

const duplicateOneDayVacancy = async () => {
  let title = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_duplicate_title')
  let text = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_duplicate_text')
  let successMessage = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_duplicate_success')
  let failureMessage = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_duplicate_fail')

  await nuxtApp.$confirm(title, text, async () => {
    let result = await nuxtApp.$postToBackend('/duplicateOneDayVacancies', { oneDayVacancyIds: [fullOneDayVacancyInfo.value.id], organisationId: nuxtApp.$globalState.toggledOrganisationId, dataToShow: 'show_all' })
    await nuxtApp.$router.push(nuxtApp.$findPath('/organisatie/eendags-vacatures-en-matches'))
    if (result.successCounter > 0) {
      nuxtApp.$toast(successMessage)
    } else {
      nuxtApp.$toastFailure(failureMessage)
    }
  })
}

const deleteOneDayVacancy = async () => {
  let title = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_delete_title')
  let text = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_delete_text')
  let successMessage = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_delete_success')
  let failureMessage = nuxtApp.$gadTranslate('page_one_day_vacancy_detail_swal_delete_fail')

  await nuxtApp.$confirm(title, text, async () => {
    let result = await nuxtApp.$postToBackend('/deleteOneDayVacancies', { oneDayVacancyIds: [fullOneDayVacancyInfo.value.id], organisationId: nuxtApp.$globalState.toggledOrganisationId, dataToShow: 'show_all' })
    await nuxtApp.$router.push(nuxtApp.$findPath('/organisatie/eendags-vacatures-en-matches'))
    if (result.successCounter > 0) {
      nuxtApp.$toast(successMessage)
    } else {
      nuxtApp.$toastFailure(failureMessage)
    }
  })
}

const oneDayVacancyFormSubmitted = async ({ publish, userCanModifyOneDayVacancy }) => {
  showEditButton.value = true
  blur.value = false
  if (publish) {
    oneDayVacancyPublishedModal.value.show()
  } else {
    await nuxtApp.$toast(nuxtApp.$gadTranslateUpper('create_edit_one_day_vacancy_overlay_one_day_vacancy_saved_as_draft_toast_message'))
  }
  if (!userCanModifyOneDayVacancy) {
    // User can not modify one day vacancy anymore. Go back to the overview of one day vacancies
    await nuxtApp.$router.push(nuxtApp.$findPath('/organisatie/eendags-vacatures-en-matches'))
  } else {
    await loadData()
  }
}

const copyOneDayVacancyLink = () => {
  let oneDayVacancyUrl = nuxtApp.$globalState.baseUrl + nuxtApp.$findPath('/eendags-vrijwilligerswerk/:oneDayVacancyName/:id', { oneDayVacancyName: fullOneDayVacancyInfo.value.name, id: fullOneDayVacancyInfo.value.id })
  navigator.clipboard.writeText(oneDayVacancyUrl)
  nuxtApp.$toast(nuxtApp.$gadTranslateUpper('general_text_copied_to_clipboard'))
}

const openOverlayRight = () => {
  showEditButton.value = false
  blur.value = true
}

const closeOverlayRight = () => {
  showEditButton.value = true
  blur.value = false
}

const openSubscriptionModal = () => {
  isLikeOnly.value = false
  oneDayVacancySubscriptionModal.value.show()
}
const openLikeModal = () => {
  isLikeOnly.value = true
  oneDayVacancySubscriptionModal.value.show()
}

const handleLikeSuccess = () => {
  fullOneDayVacancyInfo.value.isLikedByCurrentUser = true
  fullOneDayVacancyInfo.value.likeCount++
  likeKey.value++
}

const subscriptionSuccess = (amountNewSubscriptions) => {
  isUserSubscribedAlready.value = true
  fullOneDayVacancyInfo.value.amountSubscriptions += amountNewSubscriptions
}

async function userSubscribed () {
  isUserSubscribedAlready.value = true
  fullOneDayVacancyInfo.value.amountSubscriptions += 1
}

const print = async () => {
  if (fullOneDayVacancyInfo.value && fullOneDayVacancyInfo.value.status && !statusEquals(fullOneDayVacancyInfo.value.status, STATUSES.ACTIVE)) {
    await nuxtApp.$confirm(nuxtApp.$gadTranslate('page_one_day_vacancy_detail_print_to_pdf_not_active_swal_title'), nuxtApp.$gadTranslate('page_one_day_vacancy_detail_print_to_pdf_not_active_swal_text'), async () => {
      await printOneDayVacancyImpl(nuxtApp, fullOneDayVacancyInfo.value.id)
    })
  } else {
    await printOneDayVacancyImpl(nuxtApp, fullOneDayVacancyInfo.value.id)
  }
}
</script>

<style lang="scss" scoped>
.clickable-phone {
  cursor: pointer;
}

.one-day-vacancy-detail-page {
  .interest-wrapper {
    flex: 1 0 auto;

    :deep() {
      .icon-selection {
        > .icons-container {
          justify-content: flex-end;

          .base-icon {
            margin-right: 32px;
          }
        }
      }
    }
  }

  .one-day-vacancy-grid-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;
    row-gap: 20px;

    .item-holder-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;

      i {
        font-size: 1.5rem;
      }
    }
  }

  .rich-text {
    word-break: break-word;
  }

  :deep() {
    .p-chip {
      padding: 0 0.75rem;

      .p-chip-text {
        margin: 0;
      }
    }
  }
}
</style>
